import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { RefreshCw, Info, Image, Users, Settings as SettingsIcon, LayoutDashboard, MessageCircle } from "lucide-react";
import { getEventById } from "../../services/eventService";
import { deleteEvent } from "../../services/eventDetailService";
import { deactivateRoom, reactivateRoom } from "../../services/roomService";
import { useAuth } from "../../context/AuthContext";
import { deriveEventPermissions } from "../../utils/eventPermissions";
import Loader from "../../components/common/Loader";
import ConfirmModal from "../../components/common/ConfirmModal";
import Tabs from "../../components/common/Tabs";
import { color } from "../../styles/theme";
import EventHeader from "../../components/event-details/EventHeader";
import DetailsTab from "../../components/event-details/DetailsTab";
import MediaTab from "../../components/event-details/MediaTab";
import ParticipantsTab from "../../components/event-details/ParticipantsTab";
import SettingsTab from "../../components/event-details/SettingsTab";
import SummaryTab from "../../components/event-details/SummaryTab";
import QRModal from "../../components/event-details/QRModal";
import InviteBusinessAssistantModal from "../../components/event-details/InviteBusinessAssistantModal";
import QRBufferTimeModal from "../../components/event-details/QRBufferTimeModal";
import WatermarkEventDetailsModal from "../../components/event-details/WatermarkEventDetailsModal";
import WatermarkSettingsModal from "../../components/common/WatermarkSettingsModal";
import BusinessAssistantPermissionsModal from "../../components/event-details/BusinessAssistantPermissionsModal";

const TAB_DEFS = [
  { key: "Summary", labelKey: "eventDetails.tabs.summary", icon: LayoutDashboard },
  { key: "Details", labelKey: "eventDetails.tabs.details", icon: Info },
  { key: "Media", labelKey: "eventDetails.tabs.media", icon: Image },
  { key: "Participants", labelKey: "eventDetails.tabs.participants", icon: Users },
  { key: "Settings", labelKey: "eventDetails.tabs.settings", icon: SettingsIcon },
];

/**
 * Event Details page, synced with the mobile app's EventDetailsUpdated
 * screen (src/Screens/event-detail-updated/index.js) — header, tabbed
 * Details/Media/Participants view, QR generation, and promoter management
 * actions. Scoped to promoter/owner-facing behavior only; see the approved
 * plan for the full list of mobile flows intentionally left out (join/leave,
 * SignalR real-time, push notifications, background auto-upload, etc.)
 */
const EventDetails = () => {
  const { t } = useTranslation(["eventDetails", "common"]);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, subAccountContext } = useAuth();
  const isAssigned = !!location.state?.isAssigned;

  const [event, setEvent] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // Screens opened from a specific tab (e.g. Media History from Settings) pass
  // { initialTab } via navigate(..., { state }) so Back lands where it left off.
  const [activeTab, setActiveTab] = useState(location.state?.initialTab || "Details");
  const [showQR, setShowQR] = useState(false);
  const [showInviteBA, setShowInviteBA] = useState(false);
  const [showQRBufferTime, setShowQRBufferTime] = useState(false);
  const [showWatermarkEventDetails, setShowWatermarkEventDetails] = useState(false);
  const [showBAPermissions, setShowBAPermissions] = useState(false);
  const [showPromoterWatermark, setShowPromoterWatermark] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeactivateRoomConfirm, setShowDeactivateRoomConfirm] = useState(false);
  const [togglingRoomActive, setTogglingRoomActive] = useState(false);
  // Bumped on every Refresh click and used as each tab's `key` below, so
  // Refresh actually re-mounts the active tab (re-running its own data
  // fetch) instead of only updating the raw `event` object — previously the
  // only way to see fresh tab data was to switch tabs away and back, which
  // remounts them as a side effect.
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchEvent = ({ silent = false } = {}) => {
    if (!silent) setLoadingEvent(true);
    else setRefreshing(true);
    return getEventById(id)
      .then((res) => setEvent(res.data?.Result || res.data))
      .catch(() => toast.error(t("eventDetails.loadError")))
      .finally(() => {
        setLoadingEvent(false);
        setRefreshing(false);
      });
  };

  useEffect(() => {
    fetchEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loadingEvent) return <Loader />;
  if (!event) return <p style={{ color: "#888", padding: 20 }}>{t("eventDetails.notFound")}</p>;

  const permissions = deriveEventPermissions(event, user, subAccountContext);
  const visibleTabs = TAB_DEFS.filter((tab) => {
    if (tab.key === "Media") return permissions.canManageGallery || permissions.canManageSingleMedia;
    if (tab.key === "Participants" || tab.key === "Settings") return permissions.canManageEvents;
    return true;
  }).map((tab) => ({ ...tab, label: t(tab.labelKey) }));
  const effectiveActiveTab = visibleTabs.some((tab) => tab.key === activeTab) ? activeTab : "Details";

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteEvent(id);
      toast.success(t("eventDetails.deleteSuccess"));
      navigate("/events");
    } catch (err) {
      toast.error(err.response?.data?.Message || t("eventDetails.deleteError"));
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // An @Room can never be permanently deleted from the Panel — only
  // deactivated/reactivated (Event.IsClosed). Reactivating is a plain
  // reversal so it fires immediately; deactivating hides the room from
  // participants, so it goes through a confirm step like Delete does.
  const handleToggleRoomActive = async () => {
    if (event?.IsClosed) {
      setTogglingRoomActive(true);
      try {
        const res = await reactivateRoom(id);
        toast.success(res.data?.Message || t("eventDetails.roomReactivateSuccess"));
        fetchEvent({ silent: true });
      } catch (err) {
        toast.error(err.response?.data?.Message || t("eventDetails.roomReactivateError"));
      } finally {
        setTogglingRoomActive(false);
      }
      return;
    }
    setShowDeactivateRoomConfirm(true);
  };

  const confirmDeactivateRoom = async () => {
    setTogglingRoomActive(true);
    try {
      const res = await deactivateRoom(id);
      toast.success(res.data?.Message || t("eventDetails.roomDeactivateSuccess"));
      setShowDeactivateRoomConfirm(false);
      fetchEvent({ silent: true });
    } catch (err) {
      toast.error(err.response?.data?.Message || t("eventDetails.roomDeactivateError"));
    } finally {
      setTogglingRoomActive(false);
    }
  };

  const openGallery = (gallery) => {
    navigate(`/events/${id}/gallery/${gallery.Id}`, { state: { gallery, chatRoomId: id } });
  };

  return (
    <div>
      <div style={s.topBar}>
        {/* Chat room id === event id for both regular Events and @Room (see
            promoter-panel-chat notes) — this works unchanged for either. */}
        <button type="button" className="btn btn-ghost btn-sm" style={s.linkBtn} onClick={() => navigate(`/chat/${id}`)}>
          <MessageCircle size={14} strokeWidth={2.25} />
          {t("eventDetails.openChat")}
        </button>
        <button type="button" className="btn btn-ghost btn-sm" style={s.linkBtn} onClick={() => fetchEvent({ silent: true }).then(() => setRefreshKey((k) => k + 1))} disabled={refreshing}>
          <RefreshCw size={14} strokeWidth={2.25} style={refreshing ? { animation: "spin 0.9s linear infinite" } : undefined} />
          {refreshing ? t("eventDetails.refreshing") : t("eventDetails.refresh")}
        </button>
      </div>

      <EventHeader
        event={event}
        isAdmin={permissions.isAdmin}
        isAssigned={isAssigned}
        onBack={() => {
          if (location.state?.from === "history") return navigate("/history");
          // Opened from @Room's "Joined Users"/"Total Events" stats
          // (RoomHomeHeader.jsx) — return to that Room, not My Events.
          if (location.state?.from === "room" && location.state?.roomId) {
            return navigate(`/rooms/${location.state.roomId}/galleries`, { state: { room: location.state.room } });
          }
          return navigate("/events");
        }}
        onEdit={() => navigate(`/events/${id}/edit`)}
        onDelete={() => setShowDeleteConfirm(true)}
        deleting={deleting}
        onToggleRoomActive={handleToggleRoomActive}
        togglingRoomActive={togglingRoomActive}
      />

      <Tabs tabs={visibleTabs} active={effectiveActiveTab} onChange={setActiveTab} />

      <div style={s.tabContent}>
        {effectiveActiveTab === "Summary" && (
          <SummaryTab key={`summary-${refreshKey}`} eventId={id} permissions={permissions} onGoToTab={setActiveTab} />
        )}
        {effectiveActiveTab === "Details" && (
          <DetailsTab
            key={`details-${refreshKey}`}
            event={event}
            permissions={permissions}
            onOpenGallery={() => setActiveTab("Media")}
            onOpenQR={() => setShowQR(true)}
            onOpenInviteBA={() => setShowInviteBA(true)}
            onOpenQRBufferTime={() => setShowQRBufferTime(true)}
          />
        )}
        {effectiveActiveTab === "Media" && (permissions.canManageGallery || permissions.canManageSingleMedia) && <MediaTab key={`media-${refreshKey}`} event={event} eventId={id} permissions={permissions} onOpenGallery={openGallery} />}
        {effectiveActiveTab === "Participants" && (
          <ParticipantsTab key={`participants-${refreshKey}`} event={event} eventId={id} isAdmin={permissions.isAdmin} permissions={permissions} />
        )}
        {effectiveActiveTab === "Settings" && (
          <SettingsTab
            key={`settings-${refreshKey}`}
            event={event}
            eventId={id}
            permissions={permissions}
            onOpenQRBufferTime={() => setShowQRBufferTime(true)}
            onOpenWatermarkEventDetails={() => setShowWatermarkEventDetails(true)}
            onOpenPromoterWatermark={() => setShowPromoterWatermark(true)}
            onOpenInviteBA={() => setShowInviteBA(true)}
            onGoToParticipants={() => setActiveTab("Participants")}
            onOpenBAPermissions={() => setShowBAPermissions(true)}
            onDelete={() => setShowDeleteConfirm(true)}
            deleting={deleting}
          />
        )}
      </div>

      {showQR && <QRModal event={event} permissions={permissions} onClose={() => setShowQR(false)} />}

      {showInviteBA && (
        <InviteBusinessAssistantModal eventId={id} onClose={() => setShowInviteBA(false)} />
      )}

      {showBAPermissions && (
        <BusinessAssistantPermissionsModal eventId={id} onClose={() => setShowBAPermissions(false)} />
      )}

      {showQRBufferTime && (
        <QRBufferTimeModal
          eventId={id}
          onClose={() => setShowQRBufferTime(false)}
          onUpdated={() => { setShowQRBufferTime(false); }}
        />
      )}

      {showWatermarkEventDetails && (
        <WatermarkEventDetailsModal
          event={event}
          eventId={id}
          onClose={() => setShowWatermarkEventDetails(false)}
          onUpdated={() => { setShowWatermarkEventDetails(false); fetchEvent({ silent: true }); }}
        />
      )}

      {showPromoterWatermark && (
        <WatermarkSettingsModal
          onClose={() => setShowPromoterWatermark(false)}
          onSaved={() => setShowPromoterWatermark(false)}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmModal
          title={t("eventDetails.deleteConfirm.title")}
          message={t("eventDetails.deleteConfirm.message", { name: event.Name })}
          confirmText={t("common:actions.delete")}
          danger
          loading={deleting}
          onConfirm={handleDelete}
          onCancel={() => !deleting && setShowDeleteConfirm(false)}
        />
      )}

      {showDeactivateRoomConfirm && (
        <ConfirmModal
          title={t("eventDetails.deactivateRoomConfirm.title")}
          message={t("eventDetails.deactivateRoomConfirm.message", { name: event.Name })}
          confirmText={t("eventDetails.deactivateRoomConfirm.confirmText")}
          danger
          loading={togglingRoomActive}
          onConfirm={confirmDeactivateRoom}
          onCancel={() => !togglingRoomActive && setShowDeactivateRoomConfirm(false)}
        />
      )}
    </div>
  );
};

const s = {
  topBar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  linkBtn: { color: color.gray600, fontWeight: 600, padding: "6px 10px" },
  tabContent: { maxWidth: 720 },
};

export default EventDetails;
